import {
  Component, OnDestroy, OnInit, HostListener,
} from '@angular/core';
import { PERMISSION_KIDS } from 'src/app/constants/permissionKids';
import { AuthService } from 'src/app/services/auth.service';
import pkg from '../../../../package.json';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  sidebarIsOpen = true;
  coursesDropdownIsOpen = false;
  isMobileView = false;
  version = pkg.version;

  manageCoursesTab = {
    label: 'Manage Courses',
    link: 'manage-courses/index',
    icon: 'list_alt',
    subMenu: [
      {
        label: 'Course Catalog', link: 'manage-courses/course-catalog', icon: 'list_alt',
      },
      {
        label: 'Validation',
        link: 'manage-courses/validation',
        permission: PERMISSION_KIDS.VALIDATE_ATTENDANCE,
      },
      // { label: 'Calendar', link: '' },
      {
        label: 'Course Requests',
        link: 'manage-courses/course-requests',
        permission: PERMISSION_KIDS.COURSE_REQUESTS,
      },
      { label: 'Catalog Suggestions',
        icon: 'playlist_add_check',  
        link: 'manage-courses/catalog-suggestions',
        permission: PERMISSION_KIDS.COURSE_SUGGESTIONS,
      },
      {
        label: 'State Document Export',
        link: 'manage-courses/document-export',
        requireMI: true,
        permission: PERMISSION_KIDS.EXPORT_STATE_DOC,
      },
      {
        label: 'Waitlists',
        link: 'manage-courses/waitlists',
        permission: PERMISSION_KIDS.MANAGE_WAITLISTS,
      },
    ],
  };
  // TODO: update sidebar with working links once available
  menuList: { label: string; link: string; icon: string; class?: string; external?: boolean; subMenu?: { label: string; link?: string | undefined; }[] }[] = [
    // {
    //   label: 'Quick Actions', link: '', class: 'quick action', icon: 'add',
    // },
    // {
    //   label: 'Dashboard', link: 'dashboard', icon: 'dashboard',
    // },
    {
      label: 'My PD Plan', link: 'profile', icon: 'person',
    },
    this.manageCoursesTab,
    {
      label: 'Manage Staff',
      link: 'manage-staff',
      icon: 'portrait',
    },
    {
      label: 'Bulletin Board', link: 'bulletin-board', icon: 'campaign',
    },
    // {
    //   label: 'Documents',  link: '', icon: 'folder_open',
    // },
    {
      label: 'Surveys', link: 'surveys', icon: 'bar_chart',
    },
    {
      label: 'Reports', link: 'reports', icon: 'insert_chart_outlined',
    },
    {
      label: 'System Settings', link: 'system-settings', icon: 'settings',
    },
    {
      label: 'FAQ/Help', link: 'https://kalpa-help-faq.scroll.site/kalpa-help-faq', icon: 'help_center', external: true,
    },
    {
      label: 'Kalpa Support', link: 'https://cynerge.atlassian.net/servicedesk/customer/portal/133', icon: 'support_agent', external: true,
    },
  ]
  selectedMenuItem: any = this.menuList[0];
  permissions: any;
  permissionsSubscription: any;
  stateCode: string;
  currentUser: any;

  entitySubscription = this.authService.user$.subscribe((user: any) => {
    if (!user.Entity) return;

    this.stateCode = user.Entity.stateCode;
  });

  userSubscription = this.authService.user$.subscribe((user: any) => {
    if (!user) return;
    this.currentUser = user;
  });

  constructor(private authService: AuthService) { }

  // Check if bulk user update should be shown (only for users with API_ACCESS permission)
  shouldShowBulkUserUpdate(): boolean {
    // Only show for users with API_ACCESS permission
    return this.permissions?.API_ACCESS === 1;
  }

  // Update the menu list to include or remove Identity Sync based on permissions
  updateBulkUserUpdateMenuItem(): void {
    const shouldShow = this.shouldShowBulkUserUpdate();
    const bulkUserUpdateIndex = this.menuList.findIndex(item => item.label === 'Identity Sync');
    const manageStaffIndex = this.menuList.findIndex(item => item.label === 'Manage Staff');

    if (shouldShow && bulkUserUpdateIndex === -1) {
      // Add Identity Sync menu item after Manage Staff
      const bulkUserUpdateItem = {
        label: 'Identity Sync',
        link: 'bulk-user-update',
        icon: 'upload_file',
      };
      this.menuList.splice(manageStaffIndex + 1, 0, bulkUserUpdateItem);
    } else if (!shouldShow && bulkUserUpdateIndex !== -1) {
      // Remove Identity Sync menu item
      this.menuList.splice(bulkUserUpdateIndex, 1);
    }
  }

  // Reusable function to check if user has any staff management permissions
  hasStaffManagementPermissions(): boolean {
    return this.permissions?.USER_ADD === 1 ||
      this.permissions?.USER_EDIT === 1 ||
      this.permissions?.MANAGE_PRIMARY_AUTHORITY === 1 ||
      this.permissions?.MANAGE_SECONDARY_AUTHORITY === 1 ||
      this.permissions?.MANAGE_ALL_AUTHORITY === 1;
  }

  // Check if user has custom roles
  hasCustomRoles(): boolean {
    if (!this.currentUser?.Roles) return false;
    return this.currentUser.Roles.some((role: any) => role.kalpaIdentifier.includes('CUSTOM'));
  }

  // Check if user has admin permissions
  hasAdminPermissions(): boolean {
    return this.permissions?.MANAGE_ALL_AUTHORITY === 1 ||
      this.permissions?.USER_ADD === 1 ||
      this.permissions?.USER_EDIT === 1;
  }

  // Check if user can access reports
  canAccessReports(): boolean {
    return this.permissions?.RUN_DATA_REPORTS === 1 && (
      this.hasCustomRoles() ||
      this.hasAdminPermissions());
  }


  ngOnInit(): void {
    this.checkScreenSize();
    this.getSelectedTab()
    this.permissionsSubscription = this.authService.permissions$.subscribe((permissions: any) => {
      if (Object.keys(permissions).length === 0) {
        this.sidebarIsOpen = false;
        return;
      }
      this.permissions = permissions;
      
      // Add or remove Bulk User Update menu item based on permissions
      this.updateBulkUserUpdateMenuItem();
      
      // if the permission for validating courses is set as false, remove the validation card
      if (permissions?.VALIDATE_ATTENDANCE === 0) {
        this.menuList = this.menuList.map((menuItem) => {
          if (menuItem.label === 'Manage Courses') {
            menuItem.subMenu = menuItem.subMenu?.filter((subMenuItem) => subMenuItem.label !== 'Validation')
          }
          return menuItem;
        })
      }
      if (this.permissions.COURSE_SUGGESTIONS === 0) {
        // here remove the catalog suggestions menu item if the user does not have the permission
        this.menuList = this.menuList.map((item: any) => {
          if (item.subMenu) {
            item.subMenu = item.subMenu.filter((subItem: any) => subItem.label !== 'Catalog Suggestions')
          }
          return item;
        })
      }
      // here we are removing the system settings menu item if the user does not have any of the permissions
      if (!this.permissions.SYS_SETTINGS_DEFAULTS && !this.permissions.SYS_SETTINGS_TYPES && !this.permissions.SYS_SETTINGS_CATEGORIES && !this.permissions.SYS_SETTINGS_REQUIREMENTS && !this.permissions.SYS_SETTINGS_BUILDINGS && !this.permissions.SYS_SETTINGS_TAGS && !this.permissions.SYS_SETTINGS_ROLES && !this.permissions.SYS_SETTINGS_POSITIONS) {
        this.menuList = this.menuList.filter((item: any) => item.label !== 'System Settings')
      }
      if (!this.hasStaffManagementPermissions() && !this.permissions.USER_ACCOUNTS) {
        this.menuList = this.menuList.filter((item: any) => item.label !== 'Manage Staff')
      }
      if (!this.permissions.MANAGE_SURVEYS) {
        this.menuList = this.menuList.filter((item: any) => item.label !== 'Surveys')
      }
      if (!this.canAccessReports()) {
        this.menuList = this.menuList.filter((item: any) => item.label !== 'Reports')
      }
      if (!this.permissions.COURSE_REQUESTS) {
        this.menuList = this.menuList.map((item: any) => {
          if (item.subMenu) {
            item.subMenu = item.subMenu.filter((subItem: any) => subItem.label !== 'Course Requests')
          }
          return item;
        })
      }
      if (!this.permissions.EXPORT_STATE_DOC || this.stateCode !== 'MI') {
        this.menuList = this.menuList.map((item: any) => {
          if (item.label === 'Manage Courses') {
            item.subMenu = item.subMenu?.filter((subItem: any) => subItem.label !== 'State Document Export')
          }

          return item;
        })
      }

      if (!this.permissions.MANAGE_WAITLISTS) {
        this.menuList = this.menuList.map((item: any) => {
          if (item.label === 'Manage Courses') {
            item.subMenu = item.subMenu?.filter((subItem: any) => subItem.label !== 'Waitlists')
          }

          return item;
        })
      }

      // TODO - refactor for any nav item with subnavs to display the only subnav when only 1 subnav exists
      if (this.manageCoursesTab.subMenu?.length === 1) {
        const indexOfManageCourses = this.menuList.findIndex((item) => item.label === 'Manage Courses');
        const submenuItem = this.manageCoursesTab.subMenu[0];

        if (indexOfManageCourses !== -1 && submenuItem) {
          this.menuList[indexOfManageCourses] = this.manageCoursesTab.subMenu[0] as { label: string; link: string; icon: string; };
        }
      } else {
        this.manageCoursesTab.label = 'Manage Courses';
      }
    });
  }

  getSelectedTab = () => {
    // get the selected tab by referencing the current url
    const path = window.location.pathname;
    this.selectedMenuItem = this.menuList.find((menuItem) => {
      const isTopRef = path.includes(menuItem.link);

      if (isTopRef) {
        return menuItem;
      }

      if (menuItem.subMenu) {
        return menuItem.subMenu.find((subMenuItem) => path.includes(subMenuItem.link || ''));
      }

      return false;
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkScreenSize();
  }

  checkScreenSize() {
    this.isMobileView = window.innerWidth <= 1024;
    if (this.isMobileView) {
      this.sidebarIsOpen = false;
    } else {
      this.sidebarIsOpen = true;
    }
  }

  toggleSidebar = () => {
    this.sidebarIsOpen = !this.sidebarIsOpen
    if (this.sidebarIsOpen && this.coursesDropdownIsOpen) {
      this.coursesDropdownIsOpen = false;
    }
  }

  toggleDropdown = (label: any) => {
    if (label === 'courses') { this.coursesDropdownIsOpen = !this.coursesDropdownIsOpen }
  }

  ngOnDestroy() {
    this.permissionsSubscription?.unsubscribe();
    this.userSubscription?.unsubscribe();
    this.entitySubscription?.unsubscribe();
  }

}
